import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OIDC Auth Callback Page for Quick Canvass
 * 
 * This page handles the callback from the GETGRIDGATE IdP after authentication.
 * It forwards the authorization code to the backend to complete the token exchange.
 * 
 * Flow:
 * 1. IdP redirects here with ?code=...&state=...
 * 2. This page calls the backend callback endpoint
 * 3. Backend exchanges code for tokens, sets session cookie
 * 4. User is redirected to the original destination
 */
export const AuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDesc = searchParams.get('error_description');

      // Handle IdP errors
      if (error) {
        console.error('OIDC Error:', error, errorDesc);
        setStatus('error');
        setErrorMessage(errorDesc || error);
        toast.error(`Authentication failed: ${errorDesc || error}`);
        return;
      }

      // Validate required params
      if (!code || !state) {
        setStatus('error');
        setErrorMessage('Missing authorization code or state parameter');
        toast.error('Invalid callback: missing required parameters');
        return;
      }

      try {
        // Call backend callback endpoint to complete token exchange
        // The backend will exchange the code and set the session cookie
        const callbackUrl = `${API_URL}/api/quick-canvass/auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
        
        // Use fetch with redirect: 'manual' to handle the redirect ourselves
        const response = await fetch(callbackUrl, {
          method: 'GET',
          credentials: 'include', // Important: include cookies
          redirect: 'manual' // Don't auto-follow redirects
        });

        if (response.type === 'opaqueredirect' || response.status === 302 || response.status === 0) {
          // Backend returned a redirect - extract location and navigate
          // For CORS, we may need to handle this differently
          // The backend should have set the cookie
          setStatus('success');
          toast.success('Successfully authenticated!');
          
          // Navigate to dashboard or intended destination
          // In a real app, you might read the redirect location from response headers
          // For now, go to dashboard
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 500);
          return;
        }

        // If we get here, try to parse the response
        if (response.ok) {
          setStatus('success');
          toast.success('Successfully authenticated!');
          navigate('/dashboard', { replace: true });
        } else {
          // Try to get error from response
          let errorText = 'Token exchange failed';
          try {
            const data = await response.json();
            errorText = data.detail || data.error || errorText;
          } catch {
            errorText = await response.text() || errorText;
          }
          setStatus('error');
          setErrorMessage(errorText);
          toast.error(errorText);
        }
      } catch (err) {
        console.error('Callback error:', err);
        
        // Network errors might mean the redirect happened successfully
        // Check if we can access the session
        try {
          const meResponse = await fetch(`${API_URL}/api/quick-canvass/sso/me`, {
            credentials: 'include'
          });
          const meData = await meResponse.json();
          
          if (meData.authenticated) {
            setStatus('success');
            toast.success('Successfully authenticated!');
            navigate('/dashboard', { replace: true });
            return;
          }
        } catch {
          // Ignore - session check failed
        }
        
        setStatus('error');
        setErrorMessage(err.message || 'Failed to complete authentication');
        toast.error('Authentication failed');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback-error">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Authentication Failed</h2>
          <p className="text-slate-600 mb-4" data-testid="auth-callback-error-message">{errorMessage}</p>
          <div className="space-y-2">
            <a 
              href="/login" 
              className="inline-block w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              data-testid="auth-callback-login-btn"
            >
              Return to Login
            </a>
            <button
              onClick={() => window.location.href = '/quick-canvass/sso/start'}
              className="inline-block w-full px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              data-testid="auth-callback-retry-btn"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback-success">
        <div className="text-center p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Authenticated!</h2>
          <p className="text-slate-600">Redirecting to Quick Canvass...</p>
        </div>
      </div>
    );
  }

  // Processing state
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback-processing">
      <div className="text-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Completing Sign-In</h2>
        <p className="text-slate-600">Verifying your authentication...</p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
